import React from "react";
import {
  RefreshIcon,
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CashIcon,
  ReceiptRefundIcon,
  DocumentTextIcon,
  CalendarIcon,
  HashtagIcon,
} from "@heroicons/react/outline";
import Badge from "../../../../components/common/Badge";
import Button from "../../../../components/common/Button";

const PaymentTab = ({
  order,
  formatCurrency,
  formatDate,
  hasExchange,
  totalExchangeValue,
  hasRefund,
  paymentStatus,
  onUpdatePayment,
}) => {
  // Calculate payment stats
  const paymentStats = {
    grandTotal: order.orderSummary?.finalTotals?.grandTotal || 0,
    amountPaid: order.orderSummary?.amountPaid || 0,
    amountDue: Math.abs(order.orderSummary?.amountDue || 0),
    historyCount: order.payment?.paymentHistory?.length || 0,
  };

  const getPaymentStatusConfig = (status) => {
    switch (status) {
      case "COMPLETED":
        return {
          color: "green",
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          textColor: "text-green-700",
          icon: CheckCircleIcon,
        };
      case "PARTIAL":
        return {
          color: "yellow",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-200",
          textColor: "text-yellow-700",
          icon: ClockIcon,
        };
      case "FAILED":
        return {
          color: "red",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          textColor: "text-red-700",
          icon: ExclamationCircleIcon,
        };
      default:
        return {
          color: "gray",
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
          textColor: "text-gray-700",
          icon: ClockIcon,
        };
    }
  };

  const statusConfig = getPaymentStatusConfig(order.payment?.status);

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Refund Alert */}
      {hasRefund && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-sm">
          <div className="flex items-start gap-2.5 sm:gap-3">
            <div className="p-1.5 sm:p-2 rounded-lg bg-orange-100 flex-shrink-0">
              <ReceiptRefundIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-orange-900 text-sm sm:text-base mb-0.5 sm:mb-1">
                Refund Required
              </h4>
              <p className="text-xs sm:text-sm text-orange-800 leading-relaxed">
                Payment has been auto-completed since exchange value exceeds
                order total. Customer will receive a refund separately.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Status Card */}
      {paymentStatus && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-2.5 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-blue-100 flex-shrink-0">
                <CreditCardIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h4 className="font-semibold text-blue-900 text-sm sm:text-base mb-0.5 sm:mb-1">
                  Payment Status
                </h4>
                <p className="text-xs sm:text-sm text-blue-800">
                  {paymentStatus.summary?.statusText}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="primary"
              onClick={onUpdatePayment}
              className="w-full sm:w-auto py-2 sm:py-2.5 text-xs sm:text-sm"
            >
              <CashIcon className="w-4 h-4 mr-1.5" />
              Update Payment
            </Button>
          </div>
        </div>
      )}

      {/* Payment Details Card */}
      <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-4 bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-100">
          <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
            <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10">
              <CreditCardIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            </div>
            Payment Details
          </h3>
        </div>

        <div className="p-3 sm:p-4 md:p-5">
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {/* Payment Method */}
            <div className="bg-gray-50 rounded-lg sm:rounded-xl p-2.5 sm:p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <CreditCardIcon className="w-3.5 h-3.5 text-gray-400" />
                <p className="text-[10px] sm:text-xs text-gray-500 font-medium">
                  Method
                </p>
              </div>
              <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                {order.payment?.method || "Not specified"}
              </p>
            </div>

            {/* Payment Status */}
            <div
              className={`${statusConfig.bgColor} ${statusConfig.borderColor} border rounded-lg sm:rounded-xl p-2.5 sm:p-3`}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <statusConfig.icon
                  className={`w-3.5 h-3.5 ${statusConfig.textColor}`}
                />
                <p
                  className={`text-[10px] sm:text-xs font-medium ${statusConfig.textColor}`}
                >
                  Status
                </p>
              </div>
              <Badge variant={statusConfig.color} size="sm">
                {order.payment?.status || "PENDING"}
              </Badge>
            </div>

            {/* Transaction ID */}
            <div className="bg-gray-50 rounded-lg sm:rounded-xl p-2.5 sm:p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <HashtagIcon className="w-3.5 h-3.5 text-gray-400" />
                <p className="text-[10px] sm:text-xs text-gray-500 font-medium">
                  Transaction ID
                </p>
              </div>
              <p className="text-xs sm:text-sm font-semibold text-gray-900 font-mono truncate">
                {order.payment?.transactionId || "N/A"}
              </p>
            </div>

            {/* Paid At */}
            <div className="bg-gray-50 rounded-lg sm:rounded-xl p-2.5 sm:p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                <p className="text-[10px] sm:text-xs text-gray-500 font-medium">
                  Paid At
                </p>
              </div>
              <p className="text-xs sm:text-sm font-semibold text-gray-900">
                {order.payment?.paidAt ? formatDate(order.payment.paidAt) : "-"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment History */}
      {order.payment?.paymentHistory &&
        order.payment.paymentHistory.length > 0 && (
          <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-4 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-100">
              <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
                <div className="p-1.5 sm:p-2 rounded-lg bg-purple-100">
                  <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                </div>
                Payment History
                <Badge variant="gray" size="sm" className="ml-auto">
                  {paymentStats.historyCount}
                </Badge>
              </h3>
            </div>

            <div className="p-3 sm:p-4 md:p-5 space-y-2 sm:space-y-3">
              {order.payment.paymentHistory.map((payment, index) => (
                <PaymentHistoryItem
                  key={payment.id || index}
                  payment={payment}
                  formatCurrency={formatCurrency}
                  formatDate={formatDate}
                  index={index}
                  total={order.payment.paymentHistory.length}
                />
              ))}
            </div>
          </div>
        )}

      {/* Payment Summary Card */}
      <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-100">
          <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
            <div className="p-1.5 sm:p-2 rounded-lg bg-green-100">
              <DocumentTextIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            </div>
            Payment Summary
          </h3>
        </div>

        <div className="p-3 sm:p-4 md:p-5">
          <div className="space-y-2 sm:space-y-3">
            {/* Grand Total */}
            <div className="flex items-center justify-between py-2 sm:py-2.5">
              <span className="text-xs sm:text-sm text-gray-600">
                Grand Total
              </span>
              <span className="text-sm sm:text-base font-semibold text-gray-900">
                {formatCurrency(paymentStats.grandTotal)}
              </span>
            </div>

            {/* Exchange Value */}
            {hasExchange && (
              <div className="flex items-center justify-between py-2 sm:py-2.5 bg-green-50 -mx-3 sm:-mx-4 px-3 sm:px-4 rounded-lg">
                <div className="flex items-center gap-1.5 sm:gap-2 text-green-700">
                  <RefreshIcon className="w-4 h-4" />
                  <span className="text-xs sm:text-sm font-medium">
                    Exchange Value
                  </span>
                </div>
                <span className="text-sm sm:text-base font-semibold text-green-600">
                  -{formatCurrency(totalExchangeValue)}
                </span>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-gray-200" />

            {/* Amount Paid */}
            <div className="flex items-center justify-between py-2 sm:py-2.5">
              <span className="text-xs sm:text-sm text-gray-600">
                Amount Paid
              </span>
              <span className="text-sm sm:text-base font-semibold text-green-600">
                {formatCurrency(paymentStats.amountPaid)}
              </span>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200" />

            {/* Amount Due / Refund */}
            <div
              className={`flex items-center justify-between p-3 sm:p-4 -mx-3 sm:-mx-4 rounded-xl ${
                hasRefund
                  ? "bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200"
                  : paymentStats.amountDue > 0
                  ? "bg-gradient-to-r from-red-50 to-rose-50 border border-red-200"
                  : "bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200"
              }`}
            >
              <div className="flex items-center gap-2">
                {hasRefund ? (
                  <ReceiptRefundIcon className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />
                ) : paymentStats.amountDue > 0 ? (
                  <ExclamationCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                ) : (
                  <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" />
                )}
                <span
                  className={`text-sm sm:text-base font-bold ${
                    hasRefund
                      ? "text-orange-900"
                      : paymentStats.amountDue > 0
                      ? "text-red-900"
                      : "text-green-900"
                  }`}
                >
                  {hasRefund
                    ? "Refund Due"
                    : paymentStats.amountDue > 0
                    ? "Amount Due"
                    : "Fully Paid"}
                </span>
              </div>
              <span
                className={`text-lg sm:text-xl md:text-2xl font-bold ${
                  hasRefund
                    ? "text-orange-600"
                    : paymentStats.amountDue > 0
                    ? "text-red-600"
                    : "text-green-600"
                }`}
              >
                {paymentStats.amountDue > 0 || hasRefund
                  ? formatCurrency(paymentStats.amountDue)
                  : "✓"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment History Item Component
const PaymentHistoryItem = ({
  payment,
  formatCurrency,
  formatDate,
  index,
  total,
}) => {
  const isCompleted = payment.status === "COMPLETED";

  return (
    <div
      className={`relative bg-gradient-to-r ${
        isCompleted
          ? "from-green-50 to-emerald-50"
          : "from-yellow-50 to-amber-50"
      } border ${
        isCompleted ? "border-green-200" : "border-yellow-200"
      } rounded-lg sm:rounded-xl p-3 sm:p-4 overflow-hidden`}
    >
      {/* Timeline indicator */}
      <div className="absolute left-0 top-0 bottom-0 w-1 sm:w-1.5 bg-gradient-to-b from-primary to-primary/50" />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 pl-2 sm:pl-3">
        <div className="flex-1 min-w-0">
          {/* Status & Method Row */}
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
            <Badge
              variant={isCompleted ? "green" : "yellow"}
              size="sm"
              className="text-[9px] sm:text-[10px]"
            >
              {isCompleted ? (
                <CheckCircleIcon className="w-3 h-3 mr-0.5" />
              ) : (
                <ClockIcon className="w-3 h-3 mr-0.5" />
              )}
              {payment.status}
            </Badge>
            <span className="text-[10px] sm:text-xs text-gray-500 font-medium px-2 py-0.5 bg-white/50 rounded-full">
              {payment.method}
            </span>
            <span className="text-[9px] sm:text-[10px] text-gray-400">
              #{total - index}
            </span>
          </div>

          {/* Timestamp */}
          <p className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1">
            <CalendarIcon className="w-3 h-3" />
            {formatDate(payment.timestamp)}
          </p>

          {/* Notes */}
          {payment.notes && (
            <p className="text-[10px] sm:text-xs text-gray-600 mt-1.5 sm:mt-2 bg-white/50 rounded px-2 py-1 italic">
              {payment.notes}
            </p>
          )}
        </div>

        {/* Amount */}
        <div className="text-right flex-shrink-0">
          <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5">Amount</p>
          <p
            className={`text-base sm:text-lg font-bold ${
              isCompleted ? "text-green-700" : "text-yellow-700"
            }`}
          >
            {formatCurrency(payment.amount)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentTab;
